import React, {useContext, useRef} from "react";
import {DatePickerContext, useDatePicker} from "./DatePickerContext";
import PropTypes from 'prop-types';
import {CalendarIcon, ChevronLeftIcon, ChevronRightIcon} from "../../icons";
import dayjs from "dayjs";
import Input from "../input/Input";
import clsx from "clsx";
import styles from './DatePicker.module.scss';
import CloseIcon from "../../icons/Close.icon";

const daysOfWeekNames = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miercoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

const monthNames = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export const DatePicker = (props) => (
  <Input {...props}>
    <RawDatePicker {...props}/>
  </Input>
);

export default React.memo(DatePicker);

DatePicker.propTypes = {
  viewType: PropTypes.oneOf(['date', 'month', 'year']),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  format: PropTypes.string,
  max: PropTypes.object,
  min: PropTypes.object
}

DatePicker.defaultProps = {
  value: new Date(),
  onChange: (e) => {
  },
  viewType: "date",
  format: 'YYYY-MM-DD'
};

export const RawDatePicker = ({value, onChange, onBlur, viewType, format, name, error, max, min}) => {
  const focusRef = useRef();
  const ctxValue = useDatePicker({value, viewType, onChange, ref: focusRef, name, min, max, onBlur, format});

  return (
    <DatePickerContext.Provider value={ctxValue}>
      <div className="relative" ref={focusRef}>
        <div className={clsx("flex border border-gray-200 rounded", {[styles.invalid]: error}, styles.container)}>
          <input
            autoComplete="off"
            className="border-none px-4 outline-none flex-grow cursor-pointer focus:shadow-none focus:ring-offset-0 rounded-l focus:ring-offset-transparent focus:ring-transparent focus:border-transparent"
            type="text"
            name={name}
            onFocus={(e) => ctxValue.showCalendar()}
            onChange={onChange}
            onBlur={onBlur}
            value={formattedDate(value, format)}
          />
          {
            value &&
            <button
              className="rounded-r flex items-center justify-center text-sm font-semibold px-2 focus:outline-none"
              onClick={ctxValue.clearCalendar}>
              <CloseIcon className="stroke-current" stroke="#666"/>
            </button>
          }

          {
            !value &&
            <button
              className="rounded-r flex items-center justify-center text-sm font-semibold px-2 focus:outline-none"
              onClick={ctxValue.toggleCalendar}>
              <CalendarIcon className="stroke-current" fill="#666"/>
            </button>
          }
        </div>
        {
          ctxValue.isVisible &&
          <div className="absolute top-10 left-0">
            <Calendar/>
          </div>
        }
      </div>
    </DatePickerContext.Provider>
  );
};

const Calendar = React.forwardRef((props, ref) => {
  const {view} = useContext(DatePickerContext);

  let selectionComponent = null;
  switch (view) {
    case "date":
      selectionComponent = <DateSelection/>;
      break;
    case "month":
      selectionComponent = <MonthSelection/>;
      break;
    case "year":
      selectionComponent = <YearSelection/>;
      break;
  }

  return (
    <div
      className="bg-white relative shadow-lg max-w-xs w-64 p-2 rounded-lg z-10">
      {selectionComponent}
    </div>
  );
});

/**
 * Date Selection Component
 * @param props
 */
const DateSelection = (props) => {
  const {
    max,
    min,
    nextMonth,
    prevMonth,
    viewMonths,
    viewYears,
    selectDate,
    visible: {month, year},
    isSelectedDate,
  } = useContext(DatePickerContext);

  const dates = [];

  for (let i = 0; i < beginningDayOfWeek(month, year); i++) {
    dates.push(<div key={`emptybefore${i}`}/>);
  }

  for (let i = 1; i <= daysInMonth(month, year); i++) {
    const disabled = (max && dayjs(new Date(year, month, i)).isAfter(max)) || (min && dayjs(new Date(year, month, i)).isBefore(min));

    dates.push(
      <button
        key={`day${i}`}
        className={clsx('hover:bg-primary hover:bg-opacity-25 hover:text-white rounded p-1 text-sm', {
          "bg-primary text-white font-semibold": isSelectedDate(i),
          [styles.disabled]: disabled
        })}
        onClick={() => selectDate(i)}
        disabled={disabled}
        style={{textAlign: "center"}}
      >
        {i}
      </button>
    );
  }

  return (
    <div
      className="text-gray-800"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gridTemplateRows: "2rem auto",
        alignItems: "stretch",
      }}
    >
      <button className={buttonClassName} onClick={(e) => prevMonth()}>
        <ChevronLeftIcon className="stroke-current"/>
      </button>

      <button
        className={`${buttonClassName} font-semibold`}
        style={{gridColumn: "2/5"}}
        onClick={(e) => viewMonths()}
      >
        {monthNames[month]}
      </button>

      <button
        className={`${buttonClassName} font-semibold`}
        style={{gridColumn: "5/7"}}
        onClick={(e) => viewYears()}
      >
        {year}
      </button>

      <button className={buttonClassName} onClick={(e) => nextMonth()}>
        <ChevronRightIcon className="stroke-current"/>
      </button>

      {daysOfWeekNames.map((day) => (
        <div
          key={(200 + day).toString()}
          className="p-1 text-sm font-semibold"
          style={{textAlign: "center"}}
        >
          {day[0]}
        </div>
      ))}

      {dates}
    </div>
  );
};

/**
 * Month Selection Component
 * @param props
 */
const MonthSelection = (props) => {
  const {viewYears, selectMonth, nextYear, prevYear, visible} = useContext(DatePickerContext);

  return (
    <div
      className="h-48"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gridTemplateRows: "2rem auto",
        alignItems: "stretch",
      }}>
      <div className="flex" style={{gridColumn: "1/5"}}>
        <CalButton chevron="left" onClick={(e) => prevYear()}/>
        <CalButton className="flex-grow" onClick={(e) => viewYears()}>
          {visible.year}
        </CalButton>
        <CalButton chevron="right" onClick={(e) => nextYear()}/>
      </div>

      {monthNames.map((month, index) => (
        <CalButton onClick={(e) => selectMonth(index)} key={month}>
          {month.substring(0, 3)}
        </CalButton>
      ))}
    </div>
  );
};

/**
 * Year Selection Component
 * @param props
 */
const YearSelection = (props) => {
  const {
    selectYear,
    prevDecade,
    nextDecade,
    visible: {year},
  } = useContext(DatePickerContext);

  let years = [];
  let [minYear, maxYear] = [year - 6, year + 6];

  for (let i = minYear; i < maxYear; i++) {
    years.push(<CalButton onClick={(e) => selectYear(i)} key={i}>{i}</CalButton>);
  }

  return (
    <div
      className="h-48"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gridTemplateRows: "2rem auto",
        alignItems: "stretch",
      }}
    >
      <div className="flex" style={{gridColumn: "1/5"}}>
        <CalButton chevron="left" onClick={(e) => prevDecade()}/>
        <CalButton className="flex-grow">
          {`${minYear} - ${maxYear - 1}`}
        </CalButton>
        <CalButton chevron="right" onClick={(e) => nextDecade()}/>
      </div>

      {years}
    </div>
  );
};

const buttonClassName =
  "flex hover:bg-gray-200 rounded p-1 text-sm flex align-center justify-center items-center focus:outline-none";

const CalButton = (props) => {
  let children = null;

  if (props.chevron && props.chevron === "left") {
    children = <ChevronLeftIcon className="stroke-current"/>;
  } else if (props.chevron && props.chevron === "right") {
    children = <ChevronRightIcon className="stroke-current"/>;
  } else children = props.children;

  return (
    <button
      className={`${buttonClassName} ${props.className}`}
      style={props.style}
      onClick={props.onClick}>
      {children}
    </button>
  );
};

function formattedDate(date, format) {
  if (!date) return '';
  return typeof date !== 'string' ? dayjs(date).format(format) : date;
}

/**
 * Beginning of Day of Week of a Month
 * @param date
 */
function beginningDayOfWeek(m, y) {
  return new Date(y, m, 1).getDay();
}

/**
 * Days in month
 */
function daysInMonth(month, year) {
  switch (month) {
    case 0:
    case 2:
    case 4:
    case 6:
    case 7:
    case 9:
    case 11:
      return 31;
    case 1:
      return isLeapYear(year) ? 29 : 28;
    default:
      return 30;
  }
}

/**
 * Is Leap Year
 * @param year
 */
function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
